import { useState, useEffect, useMemo } from 'react'
import { PageHeader, StatCard } from '../components/UI'
import { useAuth } from '../context/AuthContext'
import { get } from '../utils/api'
import { formatCurrency, STAGES } from '../utils/helpers'
import { IconTrendingUp, IconCurrencyDollar, IconUsers, IconCalendarEvent } from '@tabler/icons-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from 'recharts'

const COLORS = ['#206bc4','#7c3aed','#f59e0b','#2fb344','#d63939','#94a3b8']

export default function ReportsPage() {
  const { canSeeFinancials } = useAuth()
  const [stats, setStats] = useState(null)
  const [leads, setLeads] = useState([])
  const [deals, setDeals] = useState([])
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([get('/dashboard').catch(()=>null),get('/leads').catch(()=>[]),get('/deals').catch(()=>[]),get('/events').catch(()=>[])])
      .then(([s,l,d,e])=>{setStats(s);setLeads(l);setDeals(d);setEvents(e)}).finally(()=>setLoading(false))
  }, [])

  const leadsBySource = stats?.lead_sources || []
  const dealsByStage = useMemo(() => (STAGES||[]).map(s=>({name:s.label,value:deals.filter(d=>d.stage===s.key).reduce((sum,d)=>sum+parseFloat(d.deal_value||0),0)})),[deals])
  const eventsByType = useMemo(() => ['Conference','Wedding','Corporate Meeting','Social Event'].map(t=>({name:t,count:events.filter(e=>e.event_type===t).length,rooms:events.filter(e=>e.event_type===t).reduce((s,e)=>s+(e.room_count||0),0)})),[events])

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>

  return (
    <>
      <PageHeader title="Reports" subtitle="Sales performance and analytics" />
      <div className="row row-cards mb-4">
        <div className="col-sm-6 col-lg-3"><StatCard label="Total Leads" value={leads.length} icon={IconUsers} color="primary" /></div>
        <div className="col-sm-6 col-lg-3"><StatCard label="Open Deals" value={deals.filter(d=>!['closed_won','closed_lost'].includes(d.stage)).length} icon={IconTrendingUp} color="purple" /></div>
        {canSeeFinancials() && <div className="col-sm-6 col-lg-3"><StatCard label="Pipeline" value={formatCurrency(deals.reduce((s,d)=>s+parseFloat(d.deal_value||0),0))} icon={IconCurrencyDollar} color="green" /></div>}
        <div className="col-sm-6 col-lg-3"><StatCard label="Events" value={events.length} icon={IconCalendarEvent} color="primary" /></div>
      </div>
      <div className="row row-deck row-cards">
        {canSeeFinancials() && <div className="col-lg-6"><div className="card"><div className="card-header"><h3 className="card-title">Monthly Revenue</h3></div>
          <div className="card-body"><ResponsiveContainer width="100%" height={240}>
            <LineChart data={stats?.revenue_by_month||[]}><XAxis dataKey="month" tick={{fontSize:12}} axisLine={false} tickLine={false} />
              <YAxis tick={{fontSize:11}} axisLine={false} tickLine={false} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={v=>formatCurrency(v)} /><Line type="monotone" dataKey="value" stroke="#206bc4" strokeWidth={2} dot={{r:4,fill:'#206bc4'}} />
            </LineChart></ResponsiveContainer></div></div></div>}
        <div className="col-lg-6"><div className="card"><div className="card-header"><h3 className="card-title">Leads by Source</h3></div>
          <div className="card-body"><ResponsiveContainer width="100%" height={240}>
            <BarChart data={leadsBySource} layout="vertical"><XAxis type="number" tick={{fontSize:11}} axisLine={false} tickLine={false} />
              <YAxis dataKey="name" type="category" tick={{fontSize:12}} axisLine={false} tickLine={false} width={80} />
              <Tooltip /><Bar dataKey="count" fill="#206bc4" radius={[0,4,4,0]} barSize={20} />
            </BarChart></ResponsiveContainer></div></div></div>
        {canSeeFinancials() && <div className="col-lg-6"><div className="card"><div className="card-header"><h3 className="card-title">Deal Pipeline by Stage</h3></div>
          <div className="card-body"><ResponsiveContainer width="100%" height={240}>
            <BarChart data={dealsByStage.filter(d=>d.value>0)}><XAxis dataKey="name" tick={{fontSize:11}} axisLine={false} tickLine={false} />
              <YAxis tick={{fontSize:11}} axisLine={false} tickLine={false} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={v=>formatCurrency(v)} /><Bar dataKey="value" radius={[4,4,0,0]}>{dealsByStage.filter(d=>d.value>0).map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]} />)}</Bar>
            </BarChart></ResponsiveContainer></div></div></div>}
        <div className="col-lg-6"><div className="card"><div className="card-header"><h3 className="card-title">Events by Type</h3></div>
          <div className="card-body"><ResponsiveContainer width="100%" height={240}>
            <PieChart><Pie data={eventsByType.filter(e=>e.count>0)} dataKey="rooms" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={50} paddingAngle={3}>
              {eventsByType.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]} />)}</Pie>
              <Tooltip formatter={(v,name)=>[`${v} rooms`,name]} /><Legend wrapperStyle={{fontSize:11}} />
            </PieChart></ResponsiveContainer></div></div></div>
      </div>
    </>
  )
}
